// EmailList component
// Displays a list of emails with optional filtering

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { EnvelopeIcon, PaperClipIcon, BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'

type EmailListProps = {
  accountId?: string
  contactId?: string
  leadId?: string
  opportunityId?: string
  onEmailClick?: (emailId: string) => void
  showLinkedEntity?: boolean
  limit?: number
}

type EmailListItem = {
  id: string
  subject: string
  fromEmail: string
  fromName: string | null
  sentAt: string | null
  hasAttachments: boolean
  accountId: string | null
  contactId: string | null
  leadId: string | null
  account: { id: string; name: string } | null
  contact: { id: string; firstName: string | null; lastName: string } | null
  lead: { id: string; firstName: string | null; lastName: string; company: string } | null
}

type EmailListResponse = {
  data: EmailListItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

async function fetchEmails(params: {
  accountId?: string
  contactId?: string
  leadId?: string
  opportunityId?: string
  page?: number
  limit?: number
}): Promise<EmailListResponse> {
  const searchParams = new URLSearchParams()
  if (params.accountId) searchParams.set('accountId', params.accountId)
  if (params.contactId) searchParams.set('contactId', params.contactId)
  if (params.leadId) searchParams.set('leadId', params.leadId)
  if (params.opportunityId) searchParams.set('opportunityId', params.opportunityId)
  if (params.page) searchParams.set('page', params.page.toString())
  if (params.limit) searchParams.set('limit', params.limit.toString())

  const response = await fetch(`/api/emails?${searchParams.toString()}`, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error('Failed to fetch emails')
  }

  return response.json()
}

export function EmailList({
  accountId,
  contactId,
  leadId,
  opportunityId,
  onEmailClick,
  showLinkedEntity = false,
  limit = 10,
}: EmailListProps) {
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['emails', { accountId, contactId, leadId, opportunityId, page, limit }],
    queryFn: () => fetchEmails({ accountId, contactId, leadId, opportunityId, page, limit }),
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-lg" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-4 text-sm text-red-600">
        Failed to load emails
      </div>
    )
  }

  if (!data?.data.length) {
    return (
      <div className="text-center py-8 text-sm text-gray-500">
        <EnvelopeIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" />
        <p>No emails yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {data.data.map((email) => (
        <div
          key={email.id}
          onClick={() => onEmailClick?.(email.id)}
          className={`p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors ${
            onEmailClick ? 'cursor-pointer' : ''
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {email.subject}
                </span>
                {email.hasAttachments && (
                  <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                )}
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                <span className="truncate">
                  {email.fromName || email.fromEmail}
                </span>
                {email.sentAt && (
                  <>
                    <span>&middot;</span>
                    <span className="flex-shrink-0">
                      {formatDistanceToNow(new Date(email.sentAt), { addSuffix: true })}
                    </span>
                  </>
                )}
              </div>

              {/* Show linked entity if requested */}
              {showLinkedEntity && (
                <div className="mt-1 flex items-center gap-1 text-xs">
                  {email.account && (
                    <span className="inline-flex items-center gap-1 text-blue-600">
                      <BuildingOfficeIcon className="h-3 w-3" />
                      {email.account.name}
                    </span>
                  )}
                  {email.contact && (
                    <span className="inline-flex items-center gap-1 text-purple-600">
                      <UserIcon className="h-3 w-3" />
                      {email.contact.firstName} {email.contact.lastName}
                    </span>
                  )}
                  {email.lead && !email.account && (
                    <span className="inline-flex items-center gap-1 text-orange-600">
                      <UserIcon className="h-3 w-3" />
                      {email.lead.firstName} {email.lead.lastName} ({email.lead.company})
                    </span>
                  )}
                  {!email.account && !email.contact && !email.lead && (
                    <span className="text-gray-400 italic">Unlinked</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Simple pagination */}
      {data.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-gray-500">
            Page {page} of {data.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
            disabled={page === data.totalPages}
            className="text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
